import { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/common/DataTable';
import { WithdrawalSummary } from '@/types';
import { exportCSV } from '@/utils/exportCSV';
import { useGetTransactionHistoryQuery } from '@/store/api/walletTransactionApi';
import { mapBackendStatusToAdmin } from '@/utils/transactionStatusMapper';
import { useState } from 'react';

export default function WithdrawalSummaryReport() {
  // ✅ Server pagination state
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // ✅ API call (convert 0-based → 1-based)
  const { data, isLoading } = useGetTransactionHistoryQuery({
    page: pageIndex + 1,
    limit: pageSize,
  });

  const rows: WithdrawalSummary[] =
    data?.data.map((tx) => {
      const isCredit = tx.type === 'Credit';

      return {
        requestId: tx.id.toString(),
        playerId:
          tx.bank?.userId?.toString() ??
          tx.upi?.userId?.toString() ??
          tx.wallet?.userId?.toString() ??
          '',
        username: tx.bank?.accoutnHolder ?? tx.upi?.name ?? tx.wallet?.user?.firstname ?? 'N/A',
        amount: tx.amount,
        transactionType: tx.type,
        method: tx.bank ? 'Bank' : tx.upi ? 'UPI' : 'Wallet',
        direction: isCredit ? 'Deposit' : 'Withdrawal',
        status: mapBackendStatusToAdmin(tx.status),
        timestamp: tx.createdAt,
      };
    }) ?? [];

  const total = data?.pagination?.totalItems ?? 0;

  const columns: ColumnDef<WithdrawalSummary>[] = [
    { accessorKey: 'requestId', header: 'Request ID' },
    { accessorKey: 'playerId', header: 'Player ID' },
    { accessorKey: 'username', header: 'User' },

    {
      accessorKey: 'transactionType',
      header: 'Type',
      cell: ({ row }) => (
        <span
          className={
            row.original.transactionType === 'Credit'
              ? 'text-green-600 font-semibold'
              : 'text-red-600 font-semibold'
          }
        >
          {row.original.transactionType}
        </span>
      ),
    },

    { accessorKey: 'direction', header: 'Purpose' },

    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ row }) => {
        const sign = row.original.transactionType === 'Credit' ? '+' : '-';
        const color = row.original.transactionType === 'Credit' ? 'text-green-600' : 'text-red-600';

        return (
          <span className={`font-semibold ${color}`}>
            {sign}₹{row.original.amount}
          </span>
        );
      },
    },

    { accessorKey: 'method', header: 'Method' },

    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const s = row.original.status;
        const color =
          s === 'Approved'
            ? 'text-green-500'
            : s === 'Pending'
              ? 'text-yellow-500'
              : 'text-red-500';

        return <span className={color}>{s}</span>;
      },
    },

    {
      accessorKey: 'timestamp',
      header: 'Timestamp',
      cell: ({ row }) => new Date(row.original.timestamp).toLocaleString('en-IN'),
    },
  ];

  const handleExport = (rows: WithdrawalSummary[]) => {
    exportCSV({
      filename: 'withdrawal_summary.csv',
      header: ['Request ID', 'Player ID', 'Username', 'Amount', 'Method', 'Status', 'Timestamp'],
      rows: rows.map((r) => [
        r.requestId,
        r.playerId,
        r.username,
        r.amount,
        r.method,
        r.status,
        r.timestamp,
      ]),
    });
  };

  return (
    <>
      <div className="flex items-center justify-between mb-10">
        <h2 className="text-3xl font-bold tracking-tight">Transaction History</h2>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        enableSelection
        enableExport
        onExport={handleExport}
        searchPlaceholder="Search withdrawals..."
        pageIndex={pageIndex}
        pageSize={pageSize}
        total={total}
        onPageChange={setPageIndex}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPageIndex(0);
        }}
      />
    </>
  );
}
